import type { OperatorIdentity } from "./operator-session.js";

type CoachWorkspaceOperatorRole = OperatorIdentity["role"];

export const COACH_WORKSPACE_VIEWS = [
  {
    view: "PERSONNEL",
    label: "助教档案",
    description: "新增、查看和维护助教档案、账号、级别及服务规则。",
    roles: ["OWNER", "MANAGER"],
  },
  {
    view: "ON_DUTY",
    label: "在岗看板",
    description: "查看当前签到、轮钟、暂停、服务中和签退状态。",
    roles: ["OWNER", "MANAGER", "CASHIER", "COACH"],
  },
  {
    view: "TODAY",
    label: "今日服务提成",
    description: "按当前营业日核对已完成服务、净服务费和提成基础。",
    roles: ["OWNER", "MANAGER", "COACH"],
  },
  {
    view: "MONTHLY",
    label: "月度小时提成",
    description: "按月份核对有效服务时间、档位、净服务费和提成。",
    roles: ["OWNER", "MANAGER", "COACH"],
  },
] as const satisfies readonly {
  view: string;
  label: string;
  description: string;
  roles: readonly CoachWorkspaceOperatorRole[];
}[];

export type CoachWorkspaceView = (typeof COACH_WORKSPACE_VIEWS)[number]["view"];
export type CoachWorkspaceViewDefinition = (typeof COACH_WORKSPACE_VIEWS)[number];

export const COACH_SESSION_OPERATIONS_VIEW = "COACH" as const;
export const COACH_RUNTIME_SESSION_QUERY_KEY = "sessionId" as const;
export const COACH_SESSION_OPERATIONS_ROLES = ["OWNER", "MANAGER", "CASHIER"] as const;
const coachSessionOperationRoles: ReadonlySet<CoachWorkspaceOperatorRole> = new Set(
  COACH_SESSION_OPERATIONS_ROLES,
);

export function coachWorkspaceViewsForRoles(
  roles: readonly CoachWorkspaceOperatorRole[],
): readonly CoachWorkspaceViewDefinition[] {
  const roleSet = new Set(roles);
  return COACH_WORKSPACE_VIEWS.filter((definition) =>
    definition.roles.some((role) => roleSet.has(role)),
  );
}

export function coachWorkspaceDefaultView(
  roles: readonly CoachWorkspaceOperatorRole[],
): CoachWorkspaceView | null {
  return (
    coachWorkspaceViewsForRoles(roles).find((definition) => definition.view === "ON_DUTY")?.view ??
    null
  );
}

export function coachWorkspaceViewForRoles(
  requested: string | null | undefined,
  roles: readonly CoachWorkspaceOperatorRole[],
): CoachWorkspaceView | null {
  const available = coachWorkspaceViewsForRoles(roles);
  const requestedView = available.find((definition) => definition.view === requested)?.view;
  return requestedView ?? coachWorkspaceDefaultView(roles);
}

export function coachWorkspaceHref(
  view: CoachWorkspaceView,
  options: { month?: string } = {},
): string {
  const query = new URLSearchParams({ view });
  const month = options.month;
  if (view === "MONTHLY" && month && /^\d{4}-(?:0[1-9]|1[0-2])$/u.test(month)) {
    query.set("month", month);
  }
  return `/coach?${query.toString()}`;
}

export function canOperateCoachSessionForRoles(
  roles: readonly CoachWorkspaceOperatorRole[],
): boolean {
  return roles.some((role) => coachSessionOperationRoles.has(role));
}

export function frontdeskCoachSessionHref(sessionId: string): string {
  return `/frontdesk?sessionId=${encodeURIComponent(sessionId)}&tab=${COACH_SESSION_OPERATIONS_VIEW}`;
}
